import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import contactImg from '../media/generated/contact.webp';
import { Section, SectionHeading, PageHero, IconBadge, Faq, fadeUp, EmbeddedForm } from '../components/ui';
import { ChatIcon, LinkNodeIcon, BriefcaseIcon, ArrowUpRightIcon } from '../components/icons';

const HelpRoutes = () => (
  <Section>
    <SectionHeading
      eyebrow="How can we help?"
      title="Find the fastest route"
      subtitle="Some questions are answered quicker on the product sites themselves — here's where to start."
    />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {[
        {
          icon: ChatIcon,
          title: 'Using Gyanbeej',
          desc: 'Questions about classes, subjects, pricing, or your account.',
          href: 'https://www.gyanbeej.com/',
          label: 'Go to Gyanbeej',
          external: true,
        },
        {
          icon: LinkNodeIcon,
          title: 'Integrating GyanSpark',
          desc: 'Technical questions, integration paths, or booking a platform demo.',
          href: 'https://www.gyanspark.com/',
          label: 'Book a demo',
          external: true,
        },
        {
          icon: BriefcaseIcon,
          title: 'Joining the team',
          desc: 'Roles, hiring process, and how to send us your background.',
          href: '/careers',
          label: 'View careers',
          external: false,
        },
      ].map((item) => (
        <motion.div key={item.title} {...fadeUp} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 flex flex-col">
          <IconBadge icon={item.icon} className="mb-4" />
          <h3 className="font-bold text-white mb-2">{item.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed mb-5 flex-1">{item.desc}</p>
          {item.external ? (
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-400 hover:underline"
            >
              {item.label} <ArrowUpRightIcon className="w-3.5 h-3.5" />
            </a>
          ) : (
            <Link to={item.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-400 hover:underline">
              {item.label} <ArrowUpRightIcon className="w-3.5 h-3.5" />
            </Link>
          )}
        </motion.div>
      ))}
    </div>
  </Section>
);

const ContactForm = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="Send a message" title="Tell us what you need" subtitle="Partnerships, press, schools, or anything else — this reaches us directly." />
    <motion.div {...fadeUp} className="max-w-3xl mx-auto bg-white/[0.04] p-6 md:p-12 rounded-3xl shadow-xl border border-white/10">
      <div className="max-w-md mx-auto">
        <p className="text-center text-sm text-white/60 mb-3">
          Just keep scrolling the page — the form and its Submit button are below.
        </p>
        <EmbeddedForm
          src="https://docs.google.com/forms/d/e/1FAIpQLSdyCs-VRDHVpOYJHPe7-NW0HIyLXHMcat11nZbXNFlvKEfiZQ/viewform?embedded=true"
          height={1650}
          title="Contact Form"
        />
      </div>
    </motion.div>
  </Section>
);

const ContactFaq = () => (
  <Section>
    <SectionHeading eyebrow="FAQ" title="Before you write in" />
    <Faq
      items={[
        {
          q: 'I have a question about my Gyanbeej account.',
          a: 'Account, subject coverage, and pricing questions are handled fastest on gyanbeej.com, where you can sign in and get support in context.',
        },
        {
          q: 'We are an EdTech platform interested in GyanSpark.',
          a: 'Book a demo directly on gyanspark.com — the team will walk through how GyanSpark fits your current stack, whether or not you already run AI features.',
        },
        {
          q: 'Are you open to partnerships with schools?',
          a: 'Yes. Use the form on this page and tell us about your school or institution and what you are trying to achieve, and we will get back to you.',
        },
        {
          q: 'How quickly will I hear back?',
          a: 'We are a small team and read everything that comes in. Expect a reply within a few working days.',
        },
      ]}
    />
  </Section>
);

const Contact = () => (
  <>
    <Seo
      title="Contact"
      description="Get in touch with Preative Solutions about Gyanbeej, GyanSpark, school partnerships, or careers."
      path="/contact"
    />
    <PageHero
      eyebrow="Get in touch"
      title="Let's talk"
      subtitle="Questions about Gyanbeej, GyanSpark, a partnership, or joining the team — we read everything that comes in."
      image={contactImg}
      imageAlt="Illustration of a paper plane made of light"
    />
    <HelpRoutes />
    <ContactForm />
    <ContactFaq />
  </>
);

export default Contact;
